import type { ConsoleEntry } from "@anota/shared";
import { LIMITS } from "@anota/shared/limits";

/**
 * Errores y avisos de la consola desde que carga el script (los de antes de abrir el widget son los
 * que más ayudan), más los errores sin capturar y los recursos que no cargan. Se guardan los últimos.
 */

const MAX = 20;
const entries: ConsoleEntry[] = [];
let installed = false;

function stringify(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/** Mensajes del propio widget y de su librería de captura: no son del sitio. */
const OWN = /^\[(Anota|SnapDOM)\]/;

export function pushEntry(level: ConsoleEntry["level"], message: string, source?: string): void {
  if (OWN.test(message)) return;
  entries.push({ level, message: redact(message).slice(0, LIMITS.consoleMessage), at: Date.now(), ...(source ? { source: source.slice(0, 500) } : {}) });
  if (entries.length > MAX) entries.shift();
}

export function installConsoleCapture(): void {
  if (installed) return;
  installed = true;
  for (const level of ["error", "warn"] as const) {
    const original = console[level];
    console[level] = function (this: Console, ...args: unknown[]) {
      try {
        pushEntry(level, args.map(stringify).join(" "));
      } catch {
        // Nunca romper la consola del sitio.
      }
      return original.apply(this, args);
    };
  }
  addEventListener(
    "error",
    (e: Event) => {
      if (e instanceof ErrorEvent) {
        pushEntry("error", e.message || stringify(e.error), e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined);
        return;
      }
      // Un <img>, <script> o <link> que no cargó (estos errores no suben: por eso se escucha en captura).
      const el = e.target as (Element & { src?: string; href?: string }) | null;
      const url = el?.src || el?.href;
      if (el && url) pushEntry("error", `No cargó <${el.tagName.toLowerCase()}>: ${url}`);
    },
    true
  );
  addEventListener("unhandledrejection", (e) => pushEntry("error", `Promesa rechazada: ${stringify(e.reason)}`));
}

export const consoleEntries = (): ConsoleEntry[] => entries.slice();

/** Lo que no debe salir de la web en una nota: JWT, "Bearer …", claves largas y emails. */
export function redact(text: string): string {
  return text
    .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, "[token]")
    .replace(/\b(Bearer|Basic|Token)\s+[\w\-.~+/=]{8,}/gi, "$1 [token]")
    .replace(/\b(sk|pk|rk|ghp|gho|github_pat|xox[abprs]|AKIA)[\w-]{12,}/g, "[clave]")
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]");
}
